import { useSession } from "@/state/SessionProvider";
import { useFavorites } from "@/hooks/useFavorites";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import placeholder from "@/assets/placeholder.png?url";

const SIZE_LABELS: Record<string, string> = {
  S: "Lille (< 4.2 m)",
  M: "Medium (4.2–4.8 m)",
  L: "Stor (4.8–5.2 m)",
  XL: "Ekstra stor (> 5.2 m)",
};

export default function Favorites() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { favorites, isLoading, removeFavorite } = useFavorites(user?.id);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-bold mb-4">Log ind for at se favoritter</h1>
          <Button onClick={() => navigate("/auth?mode=signin")}>
            Log ind
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="text-3xl font-bold mb-8">Mine Favoritter</h1>

        {isLoading && <div>Henter favoritter…</div>}

        {!isLoading && favorites?.length === 0 && (
          <div className="text-center py-16">
            <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Ingen favoritter endnu</h2>
            <p className="text-muted-foreground mb-4">
              Tilføj parkeringspladser til dine favoritter for nemt at finde dem igen
            </p>
            <Button onClick={() => navigate("/search")}>
              Find Parkeringspladser
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {favorites?.map((fav: any) => {
            const listing = fav.listings;
            if (!listing) return null;
            
            const photo = listing.photos?.[0] || placeholder;
            const priceKr = Math.round((listing.base_price_day || 0) / 100);

            return (
              <Card key={fav.id} className="overflow-hidden relative">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 z-10 bg-white/80 hover:bg-white rounded-full"
                  onClick={() => removeFavorite(listing.id)}
                >
                  <Heart className="h-5 w-5 fill-red-500 text-red-500" />
                </Button>
                <div className="aspect-[4/3] w-full bg-muted">
                  <img src={photo} alt={listing.title} className="w-full h-full object-cover" />
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg line-clamp-1">{listing.title}</CardTitle>
                  <div className="text-sm text-muted-foreground line-clamp-1">
                    {listing.address}
                  </div>
                </CardHeader>
                <CardContent className="flex items-center justify-between">
                  <div className="text-sm">
                    <div className="text-muted-foreground">{SIZE_LABELS[listing.size]}</div>
                    <div className="font-semibold">{priceKr} kr / dag</div>
                  </div>
                  <Button asChild>
                    <Link to={`/spot/${listing.id}`}>Se Detaljer</Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
